import uiI18n from "../i18n";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { AiOutlinePlus } from "react-icons/ai";
import { useTranslation } from "react-i18next";
import { variables, photoUrl } from "../Variables";

const ImageUpload = ({ initialImages, folder, onImagesChange, maxImages = 6 }) => {
  const [files, setFiles] = useState([]);
  const filesRef = useRef([]);
  const updateFiles = (next) => { filesRef.current = next; setFiles(next); };
  const [initialLoaded, setInitialLoaded] = useState(false);
  const PhotoPath = variables.PHOTO_URL;

  useEffect(() => {
    if (initialImages && !initialLoaded) {
      const initialFiles = initialImages.map((imageName) => {
        if (imageName instanceof Blob) return imageName;
        const fullUrl = photoUrl(imageName, folder);
        return {
          name: imageName,
          url: fullUrl,
          preview: fullUrl,
          isExisting: true,
        };
      });
      updateFiles(initialFiles);
      setInitialLoaded(true);
  

    }
  }, [initialImages, initialLoaded, PhotoPath, folder, onImagesChange]);
  

  const addSignatureToImage = (imageSrc, signatureText) => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        ctx.font = "30px Arial";
        ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
        ctx.fillText(signatureText, 10, img.height - 10);
        resolve(canvas.toDataURL("image/png"));
      };

      img.onerror = () => resolve(imageSrc);
      img.src = imageSrc;
    });
  };

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (filesRef.current.length + acceptedFiles.length > maxImages) {
        alert(maxImages === 6 ? uiI18n.t("ui.you_can_upload_up_to_6_images") : uiI18n.t("vacationPublication.imageLimit", { count: maxImages }));
        return;
      }

      const validFiles = acceptedFiles.filter(
        (file) => file.size <= 5 * 1024 * 1024
      );
      const invalidFiles = acceptedFiles.filter(
        (file) => file.size > 5 * 1024 * 1024
      );

      if (invalidFiles.length > 0) {
        alert(uiI18n.t("ui.some_files_exceed_the_5_mb_limit_and_will_not_be_added"));
      }

      const newFilesPromises = validFiles.map(async (file) => {
        const preview = URL.createObjectURL(file);
        const signedImageSrc = await addSignatureToImage(preview, "Lina Immo");
        return Object.assign(file, {
          preview: signedImageSrc,
          isExisting: false,
        });
      });

      const newFiles = await Promise.all(newFilesPromises);

      {
        if (filesRef.current.length + newFiles.length > maxImages) {
          alert(uiI18n.t("vacationPublication.imageLimit", { count: maxImages }));
          return;
        }
        const updatedFiles = [...filesRef.current, ...newFiles];
        const updatedAllImages = updatedFiles.map((file) =>
          file.isExisting ? file.name : file
        );

        onImagesChange(updatedAllImages); // Mise à jour correcte de allImages après ajout
        updateFiles(updatedFiles);
      }
    },
    [maxImages, onImagesChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
  });

  const removeFile = (file) => (e) => {
    e.preventDefault();
  
    {
      const updatedFiles = files.filter((f) => f !== file);
  
       onImagesChange(updatedFiles.map(f => (f.isExisting ? f.name : f)));
  
      updateFiles(updatedFiles);
    }
  };
  

  const { t } = useTranslation();

  return (
    <div className="max-w-screen-lg mx-auto p-4">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-4 ${
          isDragActive ? "border-gray-900" : "border-gray-300"
        } flex flex-col items-center`}
      >
        <input {...getInputProps()} />
        <AiOutlinePlus size={50} className="text-gray-400" />
        <p className="text-gray-900">{t("upload")}</p>
      </div>
      {maxImages === 12 && <p role="status" className="mt-3 text-sm font-semibold">{t("vacationPublication.imageCount", { count: files.length, max: maxImages })}</p>}
      <div className="mt-4">
        <ul className="sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {files.map((file, index) => (
            <div key={index} className="w-full p-2 relative">
              <img
                src={file.preview}
                alt={uiI18n.t("ui.preview")}
                className="w-full h-32 object-cover"
              />
              <button
                className="absolute top-0 end-0 p-1 bg-red-500 text-white rounded-full"
                type="button"
                onClick={removeFile(file)}
              >
                X
              </button>
            </div>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ImageUpload;
